import React, { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { getDatabase, ref, set, remove, get } from 'firebase/database';
import { Plus, Trash2, Save, AlertTriangle, Lock, ChevronLeft } from 'lucide-react';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { useMaterials } from '../../contexts/MaterialsContext';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';
import { useBuildComponentTypes } from '../../contexts/BuildComponentTypesContext';
import { generateSlug } from '../../Helpers/slugUtils';
import { recordActivity } from '../../api/activityApi';
import './MaterialsManagementPage.css';

interface MaterialUsageInfo {
    buildingKey: string;
    buildingName: string;
    componentKey: string;
    componentName: string;
}

/**
 * Materials Management Page
 *
 * Admin page for managing the config/materials collection.
 * Allows creating, editing (name only), and deleting materials.
 * Materials can only be deleted if not used in any building component.
 */
const MaterialsManagementPage: React.FC = () => {
    const { materials, getTranslation, refresh } = useMaterials();
    const { buildings } = useBuildingsData();
    const { getTranslation: getComponentTypeTranslation } = useBuildComponentTypes();

    // Refresh materials when page loads (always fetch fresh data)
    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Selection state
    const [selectedMaterialKey, setSelectedMaterialKey] = useState<string | null>(null);

    // Phones show either the material list or the open material, never both.
    // Going back returns the list to where it was scrolled.
    const isSmallScreen = useIsSmallScreen();
    const mobileView = isSmallScreen && selectedMaterialKey ? 'detail' : 'list';
    const pageRef = useRef<HTMLDivElement>(null);
    const listScrollTopRef = useRef(0);

    useLayoutEffect(() => {
        if (!isSmallScreen) return;
        const scroller = pageRef.current?.closest('.main-content');
        if (!scroller) return;
        scroller.scrollTop = mobileView === 'list' ? listScrollTopRef.current : 0;
    }, [mobileView, isSmallScreen]);

    const rememberListScroll = () => {
        listScrollTopRef.current = pageRef.current?.closest('.main-content')?.scrollTop ?? 0;
    };

    // Add new material modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [newMaterialName, setNewMaterialName] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [addError, setAddError] = useState<string | null>(null);

    // Edit state
    const [editedName, setEditedName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Delete state
    const [isCheckingUsage, setIsCheckingUsage] = useState(false);
    const [materialUsage, setMaterialUsage] = useState<MaterialUsageInfo[] | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // Sort materials alphabetically by translated name
    const sortedMaterialKeys = useMemo(() => {
        return Object.keys(materials).sort((a, b) =>
            getTranslation(a).localeCompare(getTranslation(b), 'sv')
        );
    }, [materials, getTranslation]);

    // Initialize edited name when selection changes
    useEffect(() => {
        if (selectedMaterialKey && materials[selectedMaterialKey]) {
            setEditedName(materials[selectedMaterialKey].translations?.sv || '');
        }
        // Reset states
        setSaveError(null);
        setSaveSuccess(false);
        setMaterialUsage(null);
        setDeleteError(null);
    }, [selectedMaterialKey, materials]);

    // Handle adding a new material
    const handleAddMaterial = useCallback(async () => {
        const trimmedName = newMaterialName.trim();
        if (!trimmedName) {
            setAddError('Ange ett namn för materialet');
            return;
        }
        if (trimmedName.length > 100) {
            setAddError('Namnet får inte vara längre än 100 tecken');
            return;
        }

        setIsAdding(true);
        setAddError(null);

        try {
            const db = getDatabase();

            // Generate slug key from name
            const materialKey = generateSlug(trimmedName, 50);

            // Check if key already exists
            if (materials[materialKey]) {
                setAddError('Ett material med denna nyckel finns redan. Välj ett annat namn.');
                setIsAdding(false);
                return;
            }

            const materialData = {
                translations: { sv: trimmedName }
            };

            await set(ref(db, `config/materials/${materialKey}`), materialData);
            recordActivity('settings.updated', { targetKey: materialKey, label: trimmedName });

            // Reset and close modal
            setNewMaterialName('');
            setShowAddModal(false);
            setSelectedMaterialKey(materialKey);

            // Force refresh
            refresh();
        } catch (error) {
            console.error('Error creating material:', error);
            setAddError('Kunde inte skapa materialet. Försök igen.');
        } finally {
            setIsAdding(false);
        }
    }, [newMaterialName, materials, refresh]);

    // Handle closing add modal
    const handleCloseAddModal = useCallback(() => {
        if (!isAdding) {
            setNewMaterialName('');
            setShowAddModal(false);
            setAddError(null);
        }
    }, [isAdding]);

    // Handle saving name
    const handleSaveName = useCallback(async () => {
        if (!selectedMaterialKey) return;

        const trimmedName = editedName.trim();
        if (!trimmedName) {
            setSaveError('Namn får inte vara tomt');
            return;
        }
        if (trimmedName.length > 100) {
            setSaveError('Namnet får inte vara längre än 100 tecken');
            return;
        }

        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);

        try {
            const db = getDatabase();
            await set(ref(db, `config/materials/${selectedMaterialKey}/translations/sv`), trimmedName);
            recordActivity('settings.updated', { targetKey: selectedMaterialKey, label: trimmedName });
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 2000);

            // Force refresh
            refresh();
        } catch (error) {
            console.error('Error saving material name:', error);
            setSaveError('Kunde inte spara. Försök igen.');
        } finally {
            setIsSaving(false);
        }
    }, [selectedMaterialKey, editedName, refresh]);

    // Check material usage across all buildings
    const checkUsage = useCallback(async (): Promise<MaterialUsageInfo[]> => {
        if (!selectedMaterialKey) return [];

        const db = getDatabase();
        const buildingsSnap = await get(ref(db, 'buildings'));
        const usage: MaterialUsageInfo[] = [];

        // Create a map of building keys to names from the summaries context
        const buildingNameMap = new Map<string, string>();
        if (buildings) {
            buildings.forEach(b => {
                if (b.key) {
                    buildingNameMap.set(b.key, b.name || b.key);
                }
            });
        }

        if (buildingsSnap.exists()) {
            const allBuildings = buildingsSnap.val() as Record<string, unknown> | null;
            if (allBuildings) {
                Object.entries(allBuildings).forEach(([buildingKey, building]: [string, unknown]) => {
                    if (!building || typeof building !== 'object') return;

                    // Skip buildings that have been removed (no summary exists)
                    if (!buildingNameMap.has(buildingKey)) return;

                    const b = building as Record<string, unknown>;
                    const buildingName = buildingNameMap.get(buildingKey)!;

                    // Path is buildings/{key}/structure/components
                    const structure = b.structure as Record<string, unknown> | undefined;
                    const components = structure?.components as Record<string, unknown> | undefined;
                    if (components) {
                        Object.entries(components).forEach(([componentKey, comp]: [string, unknown]) => {
                            if (!comp || typeof comp !== 'object') return;
                            const c = comp as Record<string, unknown>;
                            const material = c.material as Record<string, unknown> | undefined;
                            const materialValues = material?.value as string[] | undefined;

                            // Check if this material is in the component's materials array
                            if (materialValues && Array.isArray(materialValues) && materialValues.includes(selectedMaterialKey)) {
                                // Get component display name from specificType
                                const specificType = c.specificType as Record<string, unknown> | undefined;
                                const specificTypeValue = specificType?.value as string | undefined;
                                const componentName = specificTypeValue
                                    ? getComponentTypeTranslation(specificTypeValue)
                                    : componentKey;
                                usage.push({
                                    buildingKey,
                                    buildingName,
                                    componentKey,
                                    componentName
                                });
                            }
                        });
                    }
                });
            }
        }

        return usage;
    }, [selectedMaterialKey, buildings, getComponentTypeTranslation]);

    // Handle delete button click
    const handleDeleteClick = useCallback(async () => {
        setMaterialUsage(null);
        setDeleteError(null);
        setShowDeleteModal(true);
        setIsCheckingUsage(true);

        try {
            const usage = await checkUsage();
            setMaterialUsage(usage);
        } catch (error) {
            console.error('Error checking usage:', error);
            setDeleteError('Kunde inte kontrollera användning. Försök igen.');
            setShowDeleteModal(false);
        } finally {
            setIsCheckingUsage(false);
        }
    }, [checkUsage]);

    // Confirm deletion
    const handleConfirmDelete = useCallback(async () => {
        if (!selectedMaterialKey) return;

        setIsDeleting(true);
        setDeleteError(null);

        try {
            const db = getDatabase();
            await remove(ref(db, `config/materials/${selectedMaterialKey}`));
            recordActivity('settings.updated', { targetKey: selectedMaterialKey });
            setShowDeleteModal(false);
            setSelectedMaterialKey(null);

            // Force refresh
            refresh();
        } catch (error) {
            console.error('Error deleting material:', error);
            setDeleteError('Kunde inte radera materialet. Försök igen.');
            setShowDeleteModal(false);
        } finally {
            setIsDeleting(false);
        }
    }, [selectedMaterialKey, refresh]);

    const selectedMaterial = selectedMaterialKey ? materials[selectedMaterialKey] : null;
    const canDelete = materialUsage !== null && materialUsage.length === 0;

    return (
        <div className="materials-management-page" ref={pageRef} data-mobile-view={mobileView}>
            {/* Add Material Modal */}
            <Modal show={showAddModal} onHide={handleCloseAddModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Nytt material</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Namn</Form.Label>
                        <Form.Control
                            type="text"
                            value={newMaterialName}
                            onChange={(e) => setNewMaterialName(e.target.value)}
                            placeholder="T.ex. Granit"
                            disabled={isAdding}
                            autoFocus
                        />
                        <Form.Text className="text-muted">
                            Nyckeln genereras automatiskt från namnet.
                        </Form.Text>
                    </Form.Group>

                    {addError && (
                        <div className="alert alert-danger py-2 mb-0">{addError}</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseAddModal} disabled={isAdding}>
                        Avbryt
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddMaterial}
                        disabled={isAdding || !newMaterialName.trim()}
                    >
                        {isAdding ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Skapar...
                            </>
                        ) : (
                            'Skapa'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {canDelete ? 'Radera material' : 'Kan inte radera'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isCheckingUsage ? (
                        <div className="text-center py-4">
                            <span className="spinner-border spinner-border-sm me-2" />
                            Kontrollerar användning...
                        </div>
                    ) : canDelete ? (
                        <p>
                            Är du säker på att du vill radera materialet{' '}
                            <strong>{getTranslation(selectedMaterialKey ?? undefined)}</strong>?
                            Denna åtgärd kan inte ångras.
                        </p>
                    ) : (
                        <>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <Lock size={20} className="text-warning" />
                                <span>
                                    Materialet <strong>{getTranslation(selectedMaterialKey ?? undefined)}</strong>{' '}
                                    används i {materialUsage?.length} komponent{materialUsage?.length !== 1 ? 'er' : ''} och kan inte raderas.
                                </span>
                            </div>
                            <div className="usage-list">
                                <strong className="d-block mb-2">Används i:</strong>
                                <div className="usage-items" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {materialUsage?.slice(0, 20).map((u, idx) => (
                                        <div key={idx} className="usage-item d-flex gap-2 py-1">
                                            <span className="text-muted">{u.buildingName}</span>
                                            <span>&mdash;</span>
                                            <span>{u.componentName}</span>
                                        </div>
                                    ))}
                                    {materialUsage && materialUsage.length > 20 && (
                                        <div className="text-muted fst-italic mt-2">
                                            ... och {materialUsage.length - 20} till
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {deleteError && (
                        <div className="alert alert-danger py-2 mt-3 mb-0">{deleteError}</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        {canDelete ? 'Avbryt' : 'Stäng'}
                    </Button>
                    {canDelete && (
                        <Button
                            variant="danger"
                            onClick={handleConfirmDelete}
                            disabled={isDeleting}
                        >
                            {isDeleting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Raderar...
                                </>
                            ) : (
                                <>
                                    <Trash2 size={16} className="me-1" />
                                    Radera
                                </>
                            )}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Left Pane - Material List */}
            <div className="materials-left-pane" onClickCapture={rememberListScroll}>
                <div className="left-pane-header">
                    <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
                        <Plus size={16} className="me-1" />
                        Skapa ny
                    </Button>
                </div>

                <div className="material-list-container">
                    {sortedMaterialKeys.length === 0 ? (
                        <div className="text-muted fst-italic p-3">
                            Inga material konfigurerade
                        </div>
                    ) : (
                        <div className="material-list">
                            {sortedMaterialKeys.map(key => (
                                <div
                                    key={key}
                                    className={`material-item ${selectedMaterialKey === key ? 'selected' : ''}`}
                                    onClick={() => setSelectedMaterialKey(key)}
                                >
                                    <span className="material-item-name">{getTranslation(key)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Right Pane - Detail/Edit */}
            <div className="materials-right-pane">
                {mobileView === 'detail' && (
                    <button type="button" className="materials-back-button" onClick={() => setSelectedMaterialKey(null)}>
                        <ChevronLeft size={20} aria-hidden="true" />
                        Alla material
                    </button>
                )}
                {selectedMaterial ? (
                    <div className="material-detail">
                        <div className="material-detail-header">
                            <h4 className="mb-2">{getTranslation(selectedMaterialKey ?? undefined)}</h4>
                            <span className="badge bg-secondary font-monospace">{selectedMaterialKey}</span>
                        </div>

                        <div className="material-detail-section">
                            <div className="mb-3">
                                <label className="form-label fw-medium">Namn (svenska)</label>
                                <input
                                    type="text"
                                    className={`form-control ${saveError ? 'is-invalid' : ''}`}
                                    value={editedName}
                                    onChange={(e) => setEditedName(e.target.value)}
                                    placeholder="Namn på materialet"
                                />
                                {saveError && <div className="invalid-feedback">{saveError}</div>}
                            </div>

                            <button
                                className={`btn ${saveSuccess ? 'btn-success' : 'btn-primary'}`}
                                onClick={handleSaveName}
                                disabled={isSaving || editedName === (selectedMaterial.translations?.sv || '')}
                            >
                                {isSaving ? (
                                    <span className="spinner-border spinner-border-sm" />
                                ) : saveSuccess ? (
                                    'Sparat!'
                                ) : (
                                    <>
                                        <Save size={16} className="me-1" />
                                        Spara
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Delete section */}
                        <div className="delete-section">
                            <h6 className="text-danger d-flex align-items-center gap-2">
                                <AlertTriangle size={16} />
                                Farlig zon
                            </h6>
                            <p className="text-muted small mb-3">
                                Radering av material är endast möjligt om det inte används i någon komponent.
                            </p>
                            <button
                                className="btn btn-outline-danger"
                                onClick={handleDeleteClick}
                                disabled={isCheckingUsage || isDeleting}
                            >
                                {isCheckingUsage ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" />
                                        Kontrollerar...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={16} className="me-1" />
                                        Radera material
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="empty-state">
                        <div className="text-muted">
                            Välj ett material från listan för att visa och redigera detaljer
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MaterialsManagementPage;
